<template>
  <div class="content m-5">
    <b-loading is-full-page v-model="isLoading"></b-loading>

    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h1 class="title is-1">Seasonal Ingredients</h1>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <b-button type="is-primary" icon-left="content-save" @click="saveCurrentMonth">
            Save {{ currentMonthLabel }}
          </b-button>
        </div>
      </div>
    </div>

    <p class="mb-5 has-text-grey">
      Manage seasonal ingredients for each month. The app automatically shows the current month's ingredients to users.
    </p>

    <!-- Season tabs -->
    <b-tabs v-model="activeSeasonIndex" type="is-boxed" class="mb-5">
      <b-tab-item v-for="season in seasons" :key="season.id" :label="season.label">
        <div class="columns">
          <div
            v-for="month in season.monthData"
            :key="month.id"
            class="column is-one-third">
            <div
              class="box month-selector"
              :class="{ 'is-selected': selectedMonth === month.id }"
              @click="selectMonth(month.id)">
              <h3 class="title is-5 mb-2">{{ month.label }}</h3>
              <p class="has-text-grey is-size-7">
                {{ getIngredientCount(month.id) }} ingredients
              </p>
            </div>
          </div>
        </div>
      </b-tab-item>
    </b-tabs>

    <!-- Selected month editor -->
    <div v-if="selectedMonth && monthData[selectedMonth]" class="mt-5">
      <div class="level">
        <div class="level-left">
          <h2 class="subtitle is-3">{{ currentMonthLabel }} Ingredients</h2>
        </div>
        <div class="level-right">
          <b-button type="is-primary" size="is-small" icon-left="refresh" @click="refreshIngredients">Refresh All</b-button>
        </div>
      </div>

      <EditSeasonalIngredientsList
        ref="editIngredients"
        v-model="monthData[selectedMonth].ingredients"
        :monthId="selectedMonth"
        @input="markDirty"
      />

      <!-- Preview -->
      <div class="mt-6">
        <h3 class="subtitle is-4">Preview</h3>
        <div class="box" style="background-color: #FFFBF5;">
          <div class="is-flex is-align-items-center mb-3">
            <p class="has-text-grey-dark has-text-weight-medium is-uppercase is-size-7 mr-2">
              In Season
            </p>
            <span class="has-text-warning-dark has-text-weight-medium is-size-7">
              {{ currentMonthLabel }}
            </span>
          </div>
          <div class="tags">
            <span
              v-for="ingredient in monthData[selectedMonth].ingredients"
              :key="ingredient.id"
              class="tag is-medium ingredient-tag"
              :style="{
                backgroundColor: '#ffffff',
                border: 'none',
                color: '#363636'
              }">
              <img
                v-if="ingredient.icon_url"
                :src="ingredient.icon_url"
                class="ingredient-icon mr-1"
                :style="{ backgroundColor: ingredient.icon_color || '#7CB342' }"
              />
              <b-icon
                v-else
                icon="leaf"
                size="is-small"
                class="mr-1"
              />
              {{ ingredient.name }}
            </span>
          </div>
          <p v-if="!monthData[selectedMonth].ingredients || monthData[selectedMonth].ingredients.length === 0" class="has-text-grey">
            No ingredients added yet. Use the search above to add ingredients.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import db from '@/firebase/db'
import { getMonthsBySeason, MONTHS } from '@/data/seasonalData'
import EditSeasonalIngredientsList from '@/components/EditSeasonalIngredientsList'

export default {
  name: 'SeasonalIngredientsView',
  components: {
    EditSeasonalIngredientsList
  },
  data() {
    return {
      isLoading: true,
      isDirty: false,
      activeSeasonIndex: 0,
      selectedMonth: null,
      monthData: {}
    }
  },
  computed: {
    seasons() {
      return getMonthsBySeason()
    },
    currentMonthLabel() {
      if (!this.selectedMonth) return ''
      return MONTHS[this.selectedMonth]?.label || ''
    }
  },
  created() {
    this.loadAllMonths()
  },
  methods: {
    async loadAllMonths() {
      this.isLoading = true
      try {
        const snapshot = await db.collection('seasonal_ingredients').get()

        // Initialize empty month data for all months
        const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
        monthKeys.forEach(monthId => {
          this.$set(this.monthData, monthId, {
            month: monthId,
            month_label: MONTHS[monthId].label,
            season: MONTHS[monthId].season,
            ingredients: [],
            featured_recipe_ids: []
          })
        })

        // Load existing data from Firebase
        snapshot.docs.forEach(doc => {
          this.$set(this.monthData, doc.id, doc.data())
        })

        // Select current month by default
        const currentMonth = new Date().getMonth() // 0-11
        this.selectedMonth = monthKeys[currentMonth]

        // Set active tab to current season
        const currentSeason = MONTHS[this.selectedMonth]?.season
        this.activeSeasonIndex = this.seasons.findIndex(s => s.id === currentSeason) || 0

      } catch (error) {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: `Error loading seasonal data: ${error}`
        })
      } finally {
        this.isLoading = false
      }
    },

    selectMonth(monthId) {
      if (this.isDirty) {
        this.$buefy.dialog.confirm({
          title: 'Unsaved Changes',
          message: `You have unsaved changes for ${this.currentMonthLabel}. Discard them?`,
          confirmText: 'Discard',
          type: 'is-warning',
          onConfirm: () => {
            this.isDirty = false
            this.selectedMonth = monthId
          }
        })
      } else {
        this.selectedMonth = monthId
      }
    },

    getIngredientCount(monthId) {
      return this.monthData[monthId]?.ingredients?.length || 0
    },

    markDirty() {
      this.isDirty = true
      this.saveDebounced()
    },

    saveDebounced() {
      clearTimeout(this._saveTimeout)
      this._saveTimeout = setTimeout(() => {
        this.saveCurrentMonth()
      }, 1500)
    },

    refreshIngredients() {
      this.$refs.editIngredients.refreshIngredientsList()
    },
    async saveCurrentMonth() {
      if (!this.selectedMonth) return

      this.isLoading = true
      try {
        const data = {
          ...this.monthData[this.selectedMonth],
          updated_at: new Date()
        }

        await db.collection('seasonal_ingredients')
          .doc(this.selectedMonth)
          .set(data)

        this.isDirty = false
        this.$buefy.toast.open({
          type: 'is-success',
          message: `${this.currentMonthLabel} saved successfully!`
        })
      } catch (error) {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: `Error saving: ${error}`
        })
      } finally {
        this.isLoading = false
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      this.$buefy.dialog.confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to leave?',
        confirmText: 'Leave',
        type: 'is-warning',
        onConfirm: () => next(),
        onCancel: () => next(false)
      })
    } else {
      next()
    }
  }
}
</script>

<style scoped>
.month-selector {
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}
.month-selector:hover {
  background-color: #f5f5f5;
}
.month-selector.is-selected {
  border-color: #3273dc;
  background-color: #f0f7ff;
}
.ingredient-tag {
  border: none;
  background-color: #ffffff !important;
  color: #333333 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.ingredient-icon {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  padding: 2px;
  object-fit: contain;
}

</style>
